import {
    ChangeDetectionStrategy,
    Component,
    Signal,
    computed,
    effect,
    inject,
    input,
    model,
    output,
    signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslocoService } from '@jsverse/transloco';
import { tap } from 'rxjs';

import EN from '../i18n/en.json';
import NL from '../i18n/nl.json';
import { MenuItem, OneGoUser } from '../models';
import { HeaderService, OneGoUserService } from '../services';
import { OneGoMenuItemComponent } from './onego-menu-item.component';
import { OneGoMenuTitleIconComponent } from './onego-menu-title-icon.component';

@Component({
    selector: 'onego-header',
    imports: [RouterModule, FontAwesomeModule, OneGoMenuItemComponent, OneGoMenuTitleIconComponent],
    templateUrl: './onego-header.component.html',
    styleUrls: ['./onego-header.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OneGoHeaderComponent {
    protected readonly helper = OneGoMenuItemComponent;
    private readonly headerService = inject(HeaderService);
    private readonly userService = inject(OneGoUserService);
    private readonly translocoService = inject(TranslocoService);

    public readonly title = input.required<string>();
    public readonly menu = input.required<Array<MenuItem>>();
    public readonly languageSwitcherLanguages = input<Array<string>>();
    public readonly language = model<string>();
    public readonly loggedOut = output<void>();

    private readonly transLocoLanguage = signal<string>(
        this.translocoService.getActiveLang() ?? this.translocoService.getDefaultLang()
    );

    private activeLanguage = computed(() => {
        // If the user has a preferred language, use that;
        // otherwise, use the currentLanguage input if provided,
        // or fall back to the active language from Transloco
        const currentLanguage = this.language();
        const transLocoLanguage = this.transLocoLanguage();
        return this.user()?.language ?? currentLanguage ?? transLocoLanguage;
    });

    protected menuItems = computed(() => {
        const userName = this.userName();
        const menu = this.menu();
        const userInitials = this.userInitials();

        const languageSwitcherLanguages = this.languageSwitcherLanguages();
        const currentLanguage = this.language();
        const activeLanguage = this.activeLanguage();

        if (!languageSwitcherLanguages?.length && !currentLanguage) {
            console.warn(
                `OneGoHeaderComponent: No languages provided for language switcher.
Please provide a list of languages using the "languageSwitcherLanguages" input or set the "currentLanguage" input to enable the language switcher.
Using default active language: ${activeLanguage}`
            );
        }

        const result: Array<MenuItem> = [...menu];

        if (languageSwitcherLanguages?.length) {
            result.push({
                title: this.translocoService.translate('header.language'),
                icon: 'language',
                subItems: languageSwitcherLanguages.map((code) => ({
                    title: this.translocoService.translate(`languages.${code}`, null, code),
                    action: (): TranslocoService => this.translocoService.setActiveLang(code),
                    active: code === activeLanguage,
                })),
            });
        }

        if (userName) {
            result.push({
                title: userName,
                label: userInitials,
                subItems: [
                    {
                        title: this.translocoService.translate('header.logout'),
                        action: (): void => {
                            this.loggedOut.emit();
                            this.headerService.logout();
                        },
                    },
                ],
            });
        }

        return result;
    });

    protected readonly navbarOpen = signal<number | null>(null);

    private readonly user: Signal<OneGoUser | undefined>;
    private readonly userName = computed(() => this.user()?.fullName ?? '');
    private readonly userInitials = computed(() =>
        this.userName()
            .split(/\s+/)
            .map((namePart) => namePart.charAt(0).toUpperCase())
            .join('')
    );

    public constructor() {
        this.user = toSignal(this.userService.getMe());

        this.translocoService.setTranslation(EN, 'en', { merge: true });
        this.translocoService.setTranslation(NL, 'nl', { merge: true });

        this.translocoService.langChanges$
            .pipe(
                tap((language) => {
                    const currentLanguage = this.language();
                    if (currentLanguage !== language) {
                        this.transLocoLanguage.set(language);
                        this.language.set(language);
                    }
                }),
                takeUntilDestroyed()
            )
            .subscribe();

        effect(() => this.translocoService.setActiveLang(this.language()));
    }

    protected toggleNavbar(event: MouseEvent | TouchEvent, index: number, open?: boolean | undefined): void {
        event.preventDefault();
        event.stopPropagation();
        this.navbarOpen.update((openIndex) => {
            if (open !== undefined) {
                return open ? index : null;
            }

            return openIndex === index ? null : index;
        });
    }
}
